import React, { useState, useEffect } from 'react';
import { X, EyeOff, Eye, MessageSquare, Save } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { format } from 'date-fns';
import { sr } from 'date-fns/locale';

const DiscussionDetailPanel = ({ topic, onClose, onHide, onRestore, saving }) => {
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [moderatorNote, setModeratorNote] = useState(topic?.moderatorNote || '');
  const [savingNote, setSavingNote] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);

  useEffect(() => {
    if (topic?.id) fetchComments();
    setModeratorNote(topic?.moderatorNote || '');
  }, [topic?.id]);

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const { data, error } = await supabase?.from('discussion_comments')?.select(`
          id, body, created_at, is_active, is_visible, moderator_note, user_id,
          author:user_profiles!user_id(id, full_name)
        `)?.eq('topic_id', topic?.id)?.order('created_at', { ascending: true });
      if (error) throw error;
      setComments(data || []);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSaveNote = async () => {
    setSavingNote(true);
    try {
      const { error } = await supabase?.from('discussion_topics')?.update({ moderator_note: moderatorNote, moderated_at: new Date()?.toISOString() })?.eq('id', topic?.id);
      if (error) throw error;
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 2000);
    } catch (err) {
      console.error('Error saving note:', err);
    } finally {
      setSavingNote(false);
    }
  };

  const handleHideComment = async (commentId) => {
    try {
      await supabase?.from('discussion_comments')?.update({ is_visible: false, is_active: false })?.eq('id', commentId);
      await fetchComments();
    } catch (err) {
      console.error('Error hiding comment:', err);
    }
  };

  const handleRestoreComment = async (commentId) => {
    try {
      await supabase?.from('discussion_comments')?.update({ is_visible: true, is_active: true })?.eq('id', commentId);
      await fetchComments();
    } catch (err) {
      console.error('Error restoring comment:', err);
    }
  };

  const isTopicVisible = topic?.isActive && topic?.isVisible;

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div>
          <h3 className="font-semibold text-gray-900">Detalji diskusije</h3>
          <p className="text-xs text-gray-500">{topic?.authorName}</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors">
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Topic */}
        <div className={`rounded-lg p-3 border ${
          !isTopicVisible ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-600">
              {topic?.createdAt ? format(new Date(topic?.createdAt), 'dd.MM.yyyy HH:mm', { locale: sr }) : ''}
            </span>
            {!isTopicVisible && <span className="text-xs text-red-600 font-medium">Skriven</span>}
          </div>
          <p className="font-semibold text-gray-900 mb-1">{topic?.title}</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{topic?.body}</p>
        </div>

        {/* Moderator note */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Napomena moderatora (interna)</label>
          <textarea
            value={moderatorNote}
            onChange={(e) => setModeratorNote(e?.target?.value)}
            placeholder="Interna napomena za admin tim..."
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
          <button
            onClick={handleSaveNote}
            disabled={savingNote}
            className="mt-1 flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            <Save className="w-3.5 h-3.5" />
            {noteSaved ? 'Sačuvano!' : savingNote ? 'Čuvanje...' : 'Sačuvaj napomenu'}
          </button>
        </div>

        {/* Comments */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-4 h-4 text-gray-500" />
            <p className="text-sm font-semibold text-gray-700">Komentari ({comments?.length})</p>
          </div>
          {loadingComments ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            </div>
          ) : comments?.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">Nema komentara</p>
          ) : (
            <div className="space-y-2">
              {comments?.map((comment) => {
                const isVisible = comment?.is_visible !== false && comment?.is_active !== false;
                return (
                  <div key={comment?.id} className={`rounded-lg p-2.5 border text-sm ${
                    !isVisible ? 'bg-red-50 border-red-200 opacity-75' : 'bg-white border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-600">{comment?.author?.full_name || 'Korisnik'}</span>
                      <div className="flex items-center gap-1">
                        {!isVisible && <span className="text-xs text-red-500">Skriven</span>}
                        <span className="text-xs text-gray-400">{format(new Date(comment?.created_at), 'dd.MM HH:mm', { locale: sr })}</span>
                        <button
                          onClick={() => isVisible ? handleHideComment(comment?.id) : handleRestoreComment(comment?.id)}
                          className={`p-1 rounded transition-colors ${
                            isVisible ? 'text-gray-400 hover:text-red-500' : 'text-green-500 hover:text-green-700'
                          }`}
                          title={isVisible ? 'Sakrij komentar' : 'Vrati komentar'}
                        >
                          {isVisible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                    <p className="text-gray-700">{comment?.body}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {/* Actions */}
      <div className="p-4 border-t border-gray-200">
        {isTopicVisible ? (
          <button
            onClick={() => onHide(topic?.id)}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <EyeOff className="w-4 h-4" />
            Sakrij diskusiju
          </button>
        ) : (
          <button
            onClick={() => onRestore(topic?.id)}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Eye className="w-4 h-4" />
            Vrati diskusiju
          </button>
        )}
      </div>
    </div>
  );
};

export default DiscussionDetailPanel;
